import React from 'react'
import styled from 'styled-components'

import CardWithLabeledRowsOfButtons from 'pages/Dashboard/cards/shared/CardWithLabeledRowsOfButtons'
import { StatusButtonProps } from 'pages/Dashboard/cards/shared/StatusButton'

import { color } from 'styles/colors'
import useDeviceStatuses from 'hooks/useDeviceStatuses'

type Props = {
  recentlyDisconnectedCount: number
  disconnectedIn24HoursCount: number
  disconnectedIn7DaysCount: number
}

const mapModelNames: Record<string, string> = {
  XR724: '724',
  XL824: '824',
  XL824c: '824c',
  XL850: '850',
  XL1050: '1050',
  SC360: 'SC360',
  PIVOT: 'Pivot',
  '24P': '24P',
  '24C': '24C'
}

const DisconnectedDevicesCard = ({ recentlyDisconnectedCount, disconnectedIn24HoursCount, disconnectedIn7DaysCount }: Props) => {
  const { isDeviceLoaded, deviceModels } = useDeviceStatuses()

  const disconnectedThermostatButtons = deviceModels
    .filter((device) => device.disconnected > 0)
    .map((device) => ({
      text: mapModelNames[device.model] || device.model,
      icon: <S.ConnectionIcon src="/img/hehm-disconnected.svg" alt="Disconnected Thermostats Icon" />,
      quantity: device.disconnected,
      accentColor: color('healthCritical'),
      apptentiveEventName: `view_disconnected_${device.model.toLowerCase()}_customers`,
      gaEventName: `Disconnected ${mapModelNames[device.model] ?? device.model}`,
      filter: `disconnected,${mapModelNames[device.model] ?? device.model}`,
      tooltip: `Total number of ${mapModelNames[device.model] ?? device.model} thermostats that have been disconnected (includes "archived" thermostats).`
    }))

  const disconnectedEHMKTButtons = [
    {
      label: 'Disconnected EHMKTs',
      buttons: [
        {
          text: 'Recent',
          icon: <S.RecentlyDisconnectedIcon src="/img/hehm-disconnected.svg" alt="Recently Disconnected EHMKTs Icon" />,
          quantity: recentlyDisconnectedCount,
          accentColor: color('recentlyDisconnected'),
          apptentiveEventName: 'view_recently_disconnected_customers',
          gaEventName: 'Recently Disconnected',
          filter: 'hehm_recently_disconnected',
          tooltip: 'Total number of HVAC Performance Monitoring kits that have been disconnected for less than 24 hours.'
        },
        {
          text: '24 Hours',
          icon: <S.ConnectionIcon src="/img/hehm-disconnected-24-hours.svg" alt="Disconnected EHMKTs in 24 Hours Icon" />,
          quantity: disconnectedIn24HoursCount,
          accentColor: color('disconnected24Hours'),
          apptentiveEventName: 'view_disconnected_24_hours_customers',
          gaEventName: 'Disconnected 24 Hours',
          filter: 'hehm_disconnected_24_hrs',
          tooltip: 'Total number of HVAC Performance Monitoring kits that have been disconnected more than 24 hours.'
        },
        {
          text: '7 Days',
          icon: <S.ConnectionIcon src="/img/hehm-disconnected-7-days.svg" alt="Disconnected EHMKTs in 7 Days Icon" />,
          quantity: disconnectedIn7DaysCount,
          accentColor: color('disconnected7Days'),
          apptentiveEventName: 'view_disconnected_7_days_customers',
          gaEventName: 'Disconnected 7 Days',
          filter: 'hehm_disconnected_7_days',
          tooltip: 'Total number of HVAC Performance Monitoring kits that have been disconnected more than 7 days.'
        }
      ]
    }
  ]

  const hasDisconnectedEHMKTDevices = recentlyDisconnectedCount > 0 || disconnectedIn24HoursCount > 0 || disconnectedIn7DaysCount > 0
  const hasDisconnectedThermostats = disconnectedThermostatButtons.length > 0
  const rows: Array<{ label: string; buttons: Array<StatusButtonProps> }> = []

  if (hasDisconnectedEHMKTDevices) {
    rows.push(...disconnectedEHMKTButtons)
  }

  if (hasDisconnectedThermostats) {
    rows.push({
      label: 'Disconnected Thermostats',
      buttons: disconnectedThermostatButtons
    })
  }

  if (rows.length === 0) {
    rows.push({
      label: 'No Disconnected Devices',
      buttons: []
    })
  }

  return (
    <CardWithLabeledRowsOfButtons
      icon={<S.CardIcon src="/img/dashboard-disconnected-devices-icon.svg" alt="Disconnected Devices Icon" />}
      title={DisconnectedDevicesCard.cardTitle}
      applyAccentColorToQuantity
      rows={rows}
      isLoaded={isDeviceLoaded}
      hehmEnabled
    />
  )
}

DisconnectedDevicesCard.cardTitle = 'Disconnected Devices'
DisconnectedDevicesCard.cardKey = 'disconnected_devices'

export default DisconnectedDevicesCard

const S = {
  CardIcon: styled.img`
    margin-right: 15px;
  `,
  ConnectionIcon: styled.img`
    width: 18px;
    height: 18px;
  `,
  RecentlyDisconnectedIcon: styled.img`
    width: 18px;
    height: 18px;
    filter: brightness(0);
  `
}
